import React from 'react';
import { Info, Database } from 'lucide-react';
interface AgentData {
  name: string;
  score: number;
  riskLevel: 'Low' | 'Moderate' | 'High';
  summary: string;
  findings: string[];
  dataSources: string[];
}
const agents: AgentData[] = [{
  name: 'Fleet Risk',
  score: 55,
  riskLevel: 'Moderate',
  summary: 'Fleet is aging (7.2 years average) with recent maintenance issues flagged by DOT.',
  findings: ['3 maintenance violations in past year', 'Fleet age: 7.2 years (older than typical)', 'No fuel cost protection in place'],
  dataSources: ['FMCSA SAFER System', 'Google News']
}, {
  name: 'Warehouse Exposure',
  score: 82,
  riskLevel: 'Low',
  summary: 'Inventory moves faster than competitors. Just signed 10-year lease on new facility.',
  findings: ['New 100k sqft warehouse (10 year commitment)', 'Inventory turns 8.5x vs industry 6.2x (good)', '3 locations, 250k sqft total'],
  dataSources: ['Google News', 'LinkedIn Profiles']
}, {
  name: 'Customer Concentration',
  score: 35,
  riskLevel: 'High',
  summary: 'Single customer represents 40% of revenue. Contract expires in 6 months.',
  findings: ['40% revenue from one retailer', 'Contract renewal due in 6 months'],
  dataSources: ['Google News', 'LinkedIn Profiles']
}, {
  name: 'Licensing Compliance',
  score: 90,
  riskLevel: 'Low',
  summary: 'All licenses current across 14 states. DOT authority active with no violations.',
  findings: ['Operating licenses active in 14 states', 'DOT authority current and clean', 'No regulatory issues pending'],
  dataSources: ['Google News', 'FMCSA SAFER System']
}, {
  name: 'Financial Performance',
  score: 62,
  riskLevel: 'Moderate',
  summary: 'Revenue down 8% last year. Profit margins shrinking under cost pressure.',
  findings: ['Revenue: $11.1M (down from $12.1M)', 'Profit margin: 3.2% (was 5.1%)', 'Debt level: 1.8x equity'],
  dataSources: ['State Annual Reports', 'County Court Records']
}, {
  name: 'Business Reputation',
  score: 88,
  riskLevel: 'Low',
  summary: 'Strong customer reviews (4.2 stars). Only 2 BBB complaints, both resolved.',
  findings: ['Google Reviews: 4.2 stars (47 reviews)', 'BBB: 2 complaints in 2 years, both closed', 'No negative news coverage'],
  dataSources: ['Google Reviews', 'Yelp', 'BBB']
}];
const riskColor = (level: string) => {
  if (level === 'Low') return {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    dot: 'bg-green-500'
  };
  if (level === 'Moderate') return {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    dot: 'bg-yellow-500'
  };
  return {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    dot: 'bg-red-500'
  };
};
export function AgentsTab() {
  return <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6" data-id="element-163">
      {agents.map((agent, idx) => {
      const colors = riskColor(agent.riskLevel);
      return <div key={idx} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-164">
            {/* Header */}
            <div className="px-5 py-4 flex justify-between items-center" data-id="element-165">
              <h3 className="text-base font-bold text-gray-900" data-id="element-166">
                {agent.name}
              </h3>
              <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full border ${colors.bg} ${colors.border}`} data-id="element-167">
                <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} data-id="element-168" />
                <span className="text-sm font-bold text-gray-900" data-id="element-169">
                  {agent.score}
                </span>
                <span className={`text-[10px] font-bold uppercase ${colors.text}`} data-id="element-170">
                  {agent.riskLevel}
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 pb-4" data-id="element-171">
              <p className="text-sm text-gray-600 mb-3" data-id="element-172">{agent.summary}</p>

              <ul className="space-y-1.5" data-id="element-173">
                {agent.findings.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-174">
                    <span className="text-gray-300 mt-1" data-id="element-175">•</span>
                    {f}
                  </li>)}
              </ul>
            </div>

            {/* Sources */}
            <div className="px-5 py-2.5 border-t border-gray-100 flex items-center gap-2" data-id="element-176">
              <Database className="w-3 h-3 text-gray-300 shrink-0" data-id="element-177" />
              <div className="flex gap-1.5 flex-wrap" data-id="element-178">
                {agent.dataSources.map((source, i) => <span key={i} className="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded text-[10px] font-medium text-gray-400" data-id="element-179">
                    {source}
                  </span>)}
              </div>
            </div>
          </div>;
    })}
    </div>;
}